import type { FC } from "react";
import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useGtmVimeo } from "src/hooks/use-gtm-vimeo";
import { InlineWidget } from "react-calendly";
import useUserDataProvider from "src/contexts/snug/userData-context";

interface DemoRouterDialogProps {
  onClose?: () => void;
  open?: boolean;
  openCalendly: React.Dispatch<React.SetStateAction<boolean>>;
  openPriceSheet?: () => void;
}

export const DemoRouterDialog: FC<DemoRouterDialogProps> = (props) => {
  const { onClose, open = false } = props;
  const [showCalendly, setShowCalendly] = useState(false);
const { userData } =
    useUserDataProvider();
  useGtmVimeo('vimeo-welcome-player'); // Custom hook to track Vimeo events

  // Reset showCalendly when dialog opens
  useEffect(() => {
    if (open) {
      setShowCalendly(false);
    }
  }, [open]);

  const handleScheduleTour = () => {
    setShowCalendly(true);
  };

  const handleExploreOwn = () => {
    setShowCalendly(false);
    onClose?.();
  };

  const handleClose = () => {
    setShowCalendly(false);
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullScreen sx={{backgroundColor:'#fcfcfc'}}>
      <Box sx={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 4,
        backgroundColor:'#fcfcfc'
      }}>
        {!showCalendly ? (
          <Stack spacing={3} alignItems="center" sx={{ maxWidth: 800 }}>
            <Stack spacing={1} alignItems="center">
              <Typography variant="h5" align="center">
                Choose your path
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                How would you like to get started?
              </Typography>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ width: '100%', mt: 4 }}>
            <Box
              onClick={handleScheduleTour}
              sx={{
                flex: 1,
                p: 3,
                border: '2px solid',
                borderColor: 'divider',
                borderRadius: 1,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover',
                },
              }}
            >
              <Stack spacing={2} alignItems="center">
                <Box
                  component="img"
                  src="https://s3.us-east-2.amazonaws.com/assets.getsnug.com/files/scheduleguidedtour.png"
                  alt="Schedule a Guided Tour"
                  sx={{
                    width: 250,
                    height: 'auto',
                    borderRadius: 1,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Typography variant="subtitle2" align="center" sx={{whiteSpace:'nowrap'}}>
                  Schedule a Guided Tour
                </Typography>
              </Stack>
            </Box>

            <Box
              onClick={handleExploreOwn}
              sx={{
                flex: 1,
                p: 3,
                border: '2px solid',
                borderColor: 'divider',
                borderRadius: 1,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover',
                },
              }}
            >
              <Stack spacing={2} alignItems="center">
                <Box
                  component="img"
                  src="https://s3.us-east-2.amazonaws.com/assets.getsnug.com/files/exploreonyourown.png"
                  alt="Explore on your own"
                  sx={{
                    width: 250,
                    height: 'auto',
                    borderRadius: 1,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Typography variant="subtitle2" align="center" sx={{whiteSpace:'nowrap'}}>
                  Explore on your own
                </Typography>
              </Stack>
            </Box>
          </Stack>
        </Stack>
        ) : (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2 }}>
              <Button
                variant="text"
                size="small"
                color='inherit'
                onClick={() => setShowCalendly(false)}
              >
                ← Back
              </Button>
            </Box>
            <Stack spacing={3} alignItems="center" sx={{width:'100%'}}>
            <Stack spacing={1} alignItems="center">
              <Typography variant="h5" align="center">
                Choose your time
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                When would you like to meet with the team?
              </Typography>
            </Stack>
            
            </Stack>
            <Box sx={{ flex: 1, minHeight:'700px' }}>
              <InlineWidget
              styles={{
                  height: '700px'
                }}
                url={`https://calendly.com/financial-professionals-snug/snug-estate-planning-consult?hide_gdpr_banner=1&utm_source=dashboard&utm_content=${userData?.professional_group_role_user_data?.id}`} 
                prefill={{
          email: userData?.contact_email,
          name: userData?.full_name,
        }}
                />
            </Box>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

DemoRouterDialog.propTypes = {
  onClose: PropTypes.func,
  open: PropTypes.bool,
};
